import { useEffect, useMemo, useRef, useState } from 'react';

import { AuthorizedJsonRequest } from '../../../api/common.types';
import { getUisOverview } from '../../../api/main/api';
import {
  UisCallDirection,
  UisCallRecord,
  UisOverview
} from '../../../api/main/api.types';

const UIS_REFRESH_INTERVAL_MS = 60_000;
const UIS_CALL_LIMIT = 50;

export type UisCallFilter = 'all' | 'answered' | 'missed' | 'recorded' | 'incoming' | 'outgoing';

const UIS_FINISH_REASON_LABELS: Record<string, string> = {
  subscriber_disconnects: 'Абонент завершил разговор',
  operator_disconnects: 'Оператор завершил разговор',
  no_success_subscriber_call: 'Не удалось дозвониться',
  no_operation: 'Без дополнительного действия',
  operator_number_problems: 'Проблемы на номере оператора'
};

interface IUisSectionProps {
  title: string;
  placeholderText: string;
  overview: UisOverview | null;
  isLoading: boolean;
  errorMessage: string | null;
  activeCallId: string | null;
  activeFilter: UisCallFilter;
  onActiveCallChange: (call: UisCallGroup | null) => void;
  onFilterOptionsChange: (options: UisFilterOption[]) => void;
  onRefresh: () => void;
}

export interface UisFilterOption {
  id: UisCallFilter;
  label: string;
  count: number;
}

export interface UisCallGroup {
  id: string;
  phoneKey: string;
  phoneNumber: string | null;
  crmApplicationId: number | null;
  latestCall: UisCallRecord;
  calls: UisCallRecord[];
  answeredCalls: number;
  missedCalls: number;
  recordedCalls: number;
}

interface IUisCallDetailsPanelProps {
  group: UisCallGroup;
}

interface UseUisOverviewDataResult {
  overview: UisOverview | null;
  isLoading: boolean;
  errorMessage: string | null;
}

function formatCallDateTime(value: string | null): string {
  if (!value) {
    return 'Время не указано';
  }

  const normalizedValue = value.includes('T') ? value : value.replace(' ', 'T');
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(parsedDate);
}

function formatDuration(totalSeconds: number | null): string {
  if (totalSeconds === null || totalSeconds <= 0) {
    return '0 c';
  }

  const normalizedSeconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(normalizedSeconds / 3600);
  const minutes = Math.floor((normalizedSeconds % 3600) / 60);
  const seconds = normalizedSeconds % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} ч`);
  }

  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes} м`);
  }

  parts.push(`${seconds} c`);
  return parts.join(' ');
}

function resolveRussianPluralForm(value: number, singular: string, paucal: string, plural: string): string {
  const normalizedValue = Math.abs(value) % 100;
  const lastDigit = normalizedValue % 10;

  if (normalizedValue >= 11 && normalizedValue <= 14) {
    return plural;
  }

  if (lastDigit === 1) {
    return singular;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return paucal;
  }

  return plural;
}

export function formatUisCallCount(count: number): string {
  return `${count} ${resolveRussianPluralForm(count, 'звонок', 'звонка', 'звонков')}`;
}

function normalizeUisCrmApplicationId(applicationId: number | null | undefined): number | null {
  if (typeof applicationId !== 'number' || !Number.isFinite(applicationId) || applicationId <= 0) {
    return null;
  }

  return Math.trunc(applicationId);
}

function resolveGroupCrmApplicationId(calls: readonly UisCallRecord[]): number | null {
  for (const call of calls) {
    const crmApplicationId = normalizeUisCrmApplicationId(call.crmApplicationId);

    if (crmApplicationId !== null) {
      return crmApplicationId;
    }
  }

  return null;
}

function formatUisCrmApplicationLabel(applicationId: number | null | undefined): string {
  const normalizedApplicationId = normalizeUisCrmApplicationId(applicationId);

  return normalizedApplicationId !== null ? `Заявка № ${normalizedApplicationId}` : 'Заявка не найдена.';
}

function getDirectionModifier(direction: UisCallDirection): string {
  switch (direction) {
    case 'incoming':
      return 'incoming';
    case 'outgoing':
      return 'outgoing';
    case 'internal':
      return 'internal';
    default:
      return 'unknown';
  }
}

function formatMachineValue(value: string | null, fallback = 'Не указано'): string {
  if (!value) {
    return fallback;
  }

  const mappedValue = UIS_FINISH_REASON_LABELS[value];

  if (mappedValue) {
    return mappedValue;
  }

  return value
    .split('_')
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
}

function isUisQuotaErrorMessage(message: string | null): boolean {
  if (!message) {
    return false;
  }

  const normalizedMessage = message.toLowerCase();
  return normalizedMessage.includes('limit per minute')
    || normalizedMessage.includes('limit per hour')
    || normalizedMessage.includes('limit per day')
    || normalizedMessage.includes('лимит запросов');
}

function normalizePhoneDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

export function formatCallCardPhoneNumber(phoneNumber: string | null): string {
  if (!phoneNumber) {
    return 'Номер не определён';
  }

  const trimmedPhoneNumber = phoneNumber.trim();

  if (!trimmedPhoneNumber) {
    return 'Номер не определён';
  }

  if (trimmedPhoneNumber.startsWith('+')) {
    return trimmedPhoneNumber;
  }

  const digits = normalizePhoneDigits(trimmedPhoneNumber);

  if (digits.length === 11 && digits.startsWith('7')) {
    return `+${digits}`;
  }

  if (digits.length === 11 && digits.startsWith('8')) {
    return `+7${digits.slice(1)}`;
  }

  return trimmedPhoneNumber;
}

function buildCallGroupKey(call: UisCallRecord): string {
  const phoneNumber = call.contactPhoneNumber?.trim() ?? '';
  const normalizedDigits = normalizePhoneDigits(phoneNumber);

  if (normalizedDigits.length >= 5) {
    return `phone:${normalizedDigits}`;
  }

  if (phoneNumber) {
    return `phone:${phoneNumber.toLowerCase()}`;
  }

  return `call:${call.id}`;
}

function compareCallsByStartedAtDesc(left: UisCallRecord, right: UisCallRecord): number {
  const leftValue = left.startedAt ?? left.connectedAt ?? '';
  const rightValue = right.startedAt ?? right.connectedAt ?? '';

  if (leftValue !== rightValue) {
    return rightValue.localeCompare(leftValue);
  }

  return right.id.localeCompare(left.id);
}

export function buildUisCallGroups(calls: readonly UisCallRecord[]): UisCallGroup[] {
  const groupsMap = new Map<string, UisCallRecord[]>();

  for (const call of calls) {
    const groupKey = buildCallGroupKey(call);
    const groupCalls = groupsMap.get(groupKey) ?? [];

    groupCalls.push(call);
    groupsMap.set(groupKey, groupCalls);
  }

  return Array.from(groupsMap.entries())
    .map(([groupKey, groupCalls]) => {
      const sortedCalls = [...groupCalls].sort(compareCallsByStartedAtDesc);
      const latestCall = sortedCalls[0];

      return {
        id: groupKey,
        phoneKey: groupKey,
        phoneNumber: latestCall.contactPhoneNumber,
        crmApplicationId: resolveGroupCrmApplicationId(sortedCalls),
        latestCall,
        calls: sortedCalls,
        answeredCalls: sortedCalls.filter((call) => call.isAnswered).length,
        missedCalls: sortedCalls.filter((call) => call.isMissed).length,
        recordedCalls: sortedCalls.filter((call) => call.hasRecording).length
      };
    })
    .sort((left, right) => compareCallsByStartedAtDesc(left.latestCall, right.latestCall));
}

export function countPendingMissedUisGroups(calls: readonly UisCallRecord[]): number {
  return buildUisCallGroups(calls).filter((group) => group.latestCall.isMissed).length;
}

function getCallCardBadgeLabel(call: UisCallRecord): string {
  switch (call.direction) {
    case 'incoming':
      return call.isMissed ? 'Входящий пропущен' : call.isAnswered ? 'Входящий принят' : 'Входящий';
    case 'outgoing':
      return call.isAnswered ? 'Исходящий звонок' : 'Исходящий без ответа';
    case 'internal':
      return 'Внутренний';
    default:
      return call.statusLabel;
  }
}

function matchesCallFilter(call: UisCallRecord, filter: UisCallFilter): boolean {
  switch (filter) {
    case 'answered':
      return call.isAnswered;
    case 'missed':
      return call.isMissed;
    case 'recorded':
      return call.hasRecording;
    case 'incoming':
      return call.direction === 'incoming';
    case 'outgoing':
      return call.direction === 'outgoing';
    default:
      return true;
  }
}

function buildFilterOptions(groups: readonly UisCallGroup[]): UisFilterOption[] {
  const latestCalls = groups.map((group) => group.latestCall);

  return [
    { id: 'all', label: 'Все', count: groups.length },
    { id: 'answered', label: 'Соединённые', count: latestCalls.filter((call) => call.isAnswered).length },
    { id: 'missed', label: 'Пропущенные', count: latestCalls.filter((call) => call.isMissed).length },
    { id: 'recorded', label: 'С записями', count: latestCalls.filter((call) => call.hasRecording).length },
    { id: 'incoming', label: 'Входящие', count: latestCalls.filter((call) => call.direction === 'incoming').length },
    { id: 'outgoing', label: 'Исходящие', count: latestCalls.filter((call) => call.direction === 'outgoing').length }
  ];
}

export function useUisOverviewData(
  fetchAuthorizedJson: AuthorizedJsonRequest,
  refreshRequestVersion: number,
  enabled = true
): UseUisOverviewDataResult {
  const fetchAuthorizedJsonRef = useRef(fetchAuthorizedJson);
  const [overview, setOverview] = useState<UisOverview | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchAuthorizedJsonRef.current = fetchAuthorizedJson;
  }, [fetchAuthorizedJson]);

  useEffect(() => {
    if (!enabled) {
      setOverview(null);
      setIsLoading(false);
      setErrorMessage(null);
      return;
    }

    let isDisposed = false;

    const loadOverview = async (isInitialLoad: boolean) => {
      if (isInitialLoad) {
        setIsLoading(true);
      }

      try {
        const nextOverview = await getUisOverview(fetchAuthorizedJsonRef.current, UIS_CALL_LIMIT);

        if (isDisposed) {
          return;
        }

        setOverview(nextOverview);
        setErrorMessage(null);
      } catch (error) {
        if (isDisposed) {
          return;
        }

        setErrorMessage(error instanceof Error ? error.message : 'Не удалось загрузить данные UIS.');
      } finally {
        if (isDisposed) {
          return;
        }

        setIsLoading(false);
      }
    };

    void loadOverview(true);
    const intervalId = window.setInterval(() => {
      void loadOverview(false);
    }, UIS_REFRESH_INTERVAL_MS);

    return () => {
      isDisposed = true;
      window.clearInterval(intervalId);
    };
  }, [enabled, refreshRequestVersion]);

  return {
    overview,
    isLoading,
    errorMessage
  };
}

export function UisCallDetailsPanel({ group }: IUisCallDetailsPanelProps) {
  return (
    <div className="uis-stage">
      <div className="uis-group-status-list">
        {group.calls.map((call) => (
          <article key={call.id} className={`uis-group-status-card${call.isMissed ? ' is-missed' : ''}`}>
            <div className="uis-group-status-card__header">
              <div className="uis-call-card__badges">
                <span className={`uis-call-card__badge uis-call-card__badge--${getDirectionModifier(call.direction)}${call.isMissed ? ' uis-call-card__badge--missed' : ''}`}>
                  {getCallCardBadgeLabel(call)}
                </span>
              </div>

              <span className="uis-group-status-card__timestamp">{formatCallDateTime(call.startedAt)}</span>
            </div>

            {call.hasRecording ? (
              call.recordingUrl ? (
                <div className="uis-group-status-card__recording">
                  <div className="uis-group-status-card__recording-header">
                    <strong>Запись разговора</strong>
                  </div>

                  <audio className="uis-recording-panel__player" controls preload="none" src={call.recordingUrl}>
                    Ваш браузер не поддерживает воспроизведение аудио.
                  </audio>
                </div>
              ) : (
                <div className="uis-group-status-card__note">UIS отметил запись, но не вернул playback URL для этого статуса.</div>
              )
            ) : null}

            <div className="uis-call-card__details-grid uis-group-status-card__grid">
              <div className="uis-call-card__detail">
                <span>Ожидание</span>
                <strong className="uis-call-card__detail-value">{formatDuration(call.waitDurationSeconds)}</strong>
              </div>
              <div className="uis-call-card__detail">
                <span>Разговор</span>
                <strong className="uis-call-card__detail-value">{formatDuration(call.talkDurationSeconds ?? call.totalDurationSeconds)}</strong>
              </div>
              <div className="uis-call-card__detail">
                <span>Коммуникация</span>
                <strong className="uis-call-card__detail-value">{call.communicationId ?? 'Не определена'}</strong>
              </div>
              <div className="uis-call-card__detail">
                <span>Завершение</span>
                <strong className="uis-call-card__detail-value">{formatMachineValue(call.finishReason)}</strong>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

/** Умный раздел UIS с краткой сводкой по звонкам, сотрудникам и детальной раскладкой по плечам. */
export function UisSection({
  title,
  placeholderText,
  overview,
  isLoading,
  errorMessage,
  activeCallId,
  activeFilter,
  onActiveCallChange,
  onFilterOptionsChange,
  onRefresh
}: IUisSectionProps) {
  const callGroups = useMemo(() => buildUisCallGroups(overview?.calls ?? []), [overview?.calls]);

  const filterOptions = useMemo(() => buildFilterOptions(callGroups), [callGroups]);

  const filteredGroups = useMemo(
    () => callGroups.filter((group) => matchesCallFilter(group.latestCall, activeFilter)),
    [activeFilter, callGroups]
  );

  useEffect(() => {
    onFilterOptionsChange(filterOptions);
  }, [filterOptions, onFilterOptionsChange]);

  useEffect(() => {
    if (!overview) {
      if (activeCallId !== null) {
        onActiveCallChange(null);
      }

      return;
    }

    if (activeCallId === null) {
      return;
    }

    const nextActiveCall = filteredGroups.find((group) => group.id === activeCallId) ?? null;
    onActiveCallChange(nextActiveCall);
  }, [activeCallId, filteredGroups, onActiveCallChange, overview]);

  const activeFilterLabel = useMemo(
    () => filterOptions.find((option) => option.id === activeFilter)?.label ?? 'Все',
    [activeFilter, filterOptions]
  );

  if (isLoading && !overview) {
    return (
      <div className="telegram-chat-list__placeholder">
        <span className="telegram-chat-list__pulse" aria-hidden="true" />
        <strong>Загружаем {title}</strong>
        <p>Запрашиваем последние звонковые сессии и сводку из Data API UIS.</p>
      </div>
    );
  }

  if (!overview && errorMessage) {
    const isQuotaError = isUisQuotaErrorMessage(errorMessage);

    return (
      <div className="telegram-chat-list__placeholder">
        <span className="telegram-chat-list__pulse" aria-hidden="true" />
        <strong>{isQuotaError ? 'Лимит UIS Data API исчерпан' : `${title} временно недоступен`}</strong>
        <p>{errorMessage}</p>
        <button
          type="button"
          className="uis-section__action"
          onClick={onRefresh}
        >
          Повторить загрузку
        </button>
      </div>
    );
  }

  return (
    <div className="telegram-chat-list__items uis-section">
      {errorMessage ? (
        <section className="uis-section__alert" role="status">
          {errorMessage}
        </section>
      ) : null}

      {overview && overview.calls.length === 0 ? (
        <div className="telegram-chat-list__placeholder telegram-chat-list__placeholder--compact uis-section__empty-state">
          <strong>Звонки UIS пока не найдены</strong>
          <p>{placeholderText}</p>
        </div>
      ) : null}

      {overview && overview.calls.length > 0 && filteredGroups.length === 0 ? (
        <div className="telegram-chat-list__placeholder telegram-chat-list__placeholder--compact uis-section__empty-state">
          <strong>По фильтру «{activeFilterLabel}» номера не найдены</strong>
          <p>Снимите фильтр или дождитесь следующего обновления UIS.</p>
        </div>
      ) : null}

      {filteredGroups.map((group) => {
        const call = group.latestCall;
        const isSelected = group.id === activeCallId;

        return (
        <article
          key={group.id}
          className={`uis-call-card${call.isMissed ? ' is-missed' : ''}${isSelected ? ' is-selected' : ''}`}
          role="button"
          tabIndex={0}
          aria-pressed={isSelected}
          onClick={() => onActiveCallChange(group)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onActiveCallChange(group);
            }
          }}
        >
          <div className="uis-call-card__header">
            <div className="uis-call-card__badges">
              <span className={`uis-call-card__badge${call.isMissed ? ' uis-call-card__badge--missed' : ''}`}>
                {getCallCardBadgeLabel(call)}
              </span>
            </div>

            <strong className="uis-call-card__phone">{formatCallCardPhoneNumber(call.contactPhoneNumber)}</strong>
          </div>

          <div className="uis-call-card__footer uis-call-card__footer--compact">
            <span className="uis-call-card__footer-primary">{formatUisCrmApplicationLabel(group.crmApplicationId)}</span>
            <span className="uis-call-card__footer-secondary">{formatCallDateTime(call.startedAt)}</span>
          </div>
        </article>
        );
      })}
    </div>
  );
}
